import type { AssistantDeviceItem, AssistantMemoryItem, AssistantOverview, AssistantPairing } from "@nmt/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { del, errorMessage, get, patch, post, put } from "../api";
import { SuggestionRow } from "../components/assistant";
import { Alert, Badge, Button, Checkbox, Dialog, Empty, Field, Loading, PageHeader, Panel, PanelHeader, ago, cx } from "../components/ui";

/** Your personal assistant: Fred's suggestions from your texts, what he knows about you, and your Mac. Only you see this page's contents. */
export function AssistantPage() {
  const q = useQuery({ queryKey: ["assistant"], queryFn: () => get<AssistantOverview>("/api/assistant"), refetchInterval: 30_000 });
  const d = q.data;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Assistant" description="Fred reads the texts on your phone (through your Mac), suggests what to do in the CRM, and learns how you like things. Only you see your texts, suggestions, and what Fred knows about you." />
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <Alert>{errorMessage(q.error)}</Alert>
      ) : (
        <>
          {!d!.available && <Alert tone="info">Fred isn't set up on this server, so he won't look at texts here.</Alert>}
          <Panel>
            <PanelHeader title={`Suggestions${d!.suggestions.length ? ` (${d!.suggestions.length})` : ""}`} description="Open one with Fred to confirm what he proposed, change it, or ask him something." />
            {d!.suggestions.length ? (
              <ul>
                {d!.suggestions.map((s) => (
                  <SuggestionRow key={s.id} s={s} />
                ))}
              </ul>
            ) : (
              <Empty>{d!.devices.length ? "Nothing right now. New ones show up here and on your dashboard, with a notification." : "Connect your Mac below to start."}</Empty>
            )}
          </Panel>
          {d!.failedReplies.length > 0 && <FailedReplies items={d!.failedReplies} />}
          <Memory items={d!.memory} />
          <Macs devices={d!.devices} />
          <Prefs prefs={d!.prefs} />
        </>
      )}
    </div>
  );
}

function FailedReplies({ items }: { items: AssistantOverview["failedReplies"] }) {
  const qc = useQueryClient();
  const act = async (fn: () => Promise<unknown>) => {
    await fn().catch(() => undefined);
    void qc.invalidateQueries({ queryKey: ["assistant"] });
  };
  return (
    <Panel>
      <PanelHeader title="Replies that didn't send" description="From your number, through your Mac. Send again once the Mac is on and connected." />
      <ul>
        {items.map((r) => (
          <li key={r.id} className="flex flex-col gap-2 border-b border-mist px-5 py-3 last:border-0 sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1 text-sm">
              <div>
                <span className="font-semibold">To {r.who}:</span> {r.text}
              </div>
              <div className="text-xs text-danger">
                {r.error || "It didn't go out."} · {ago(r.at)}
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button variant="primary" className="px-3 py-1" onClick={() => void act(() => post(`/api/assistant/replies/${r.id}/retry`))}>
                Send again
              </Button>
              <Button className="px-3 py-1" onClick={() => void act(() => del(`/api/assistant/replies/${r.id}`))}>
                Drop it
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function Memory({ items }: { items: AssistantMemoryItem[] }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["assistant"] });
  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      refresh();
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const proposed = items.filter((m) => m.status === "proposed");
  const active = items.filter((m) => m.status === "active");
  return (
    <Panel>
      <PanelHeader title="What Fred knows about you" description={'Private: used only when Fred works for you. Tell him "remember…" in a chat, add it here, or keep what he learned.'} />
      <div className="flex flex-col gap-3 px-5 py-4">
        {error && <Alert>{error}</Alert>}
        {proposed.length > 0 && (
          <div className="rounded-[var(--radius-control)] border border-brand/30 bg-brand-wash/40 p-3">
            <div className="mb-2 text-sm font-semibold text-ink">Fred thinks he learned</div>
            <ul className="flex flex-col gap-2">
              {proposed.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="min-w-0 flex-1">{m.text}</span>
                  <Button variant="primary" className="px-3 py-1" onClick={() => void run(() => patch(`/api/assistant/memory/${m.id}`, { status: "active" }))}>
                    Keep
                  </Button>
                  <Button className="px-3 py-1" onClick={() => void run(() => del(`/api/assistant/memory/${m.id}`))}>
                    No
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {active.length ? (
          <ul className="flex flex-col divide-y divide-mist">
            {active.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                {editing === m.id ? (
                  <>
                    <input className="min-w-0 flex-1 rounded-[var(--radius-control)] border border-line px-3 py-1.5 text-base sm:text-[15px]" value={draft} maxLength={500} onChange={(e) => setDraft(e.target.value)} />
                    <Button variant="primary" className="px-3 py-1" disabled={!draft.trim()} onClick={() => void run(() => patch(`/api/assistant/memory/${m.id}`, { text: draft.trim() })).then(() => setEditing(null))}>
                      Save
                    </Button>
                    <Button className="px-3 py-1" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="min-w-0 flex-1">{m.text}</span>
                    {m.source === "learned" && <Badge tone="off">Learned</Badge>}
                    <button className="text-xs font-semibold text-brand hover:underline" onClick={() => (setEditing(m.id), setDraft(m.text))}>
                      Edit
                    </button>
                    <button className="text-xs font-semibold text-danger hover:underline" onClick={() => void run(() => del(`/api/assistant/memory/${m.id}`))}>
                      Forget
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted">Nothing yet. For example: "Sarah is my wife; family plans come first." "Ignore the fantasy football group." "Jason handles Moore."</p>
        )}
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) void run(() => post("/api/assistant/memory", { text: text.trim() })).then(() => setText(""));
          }}
        >
          <input className="min-w-0 flex-1 rounded-[var(--radius-control)] border border-line px-3 py-2 text-base sm:text-[15px]" placeholder="Something Fred should know about you…" value={text} maxLength={500} onChange={(e) => setText(e.target.value)} />
          <Button type="submit" disabled={!text.trim()}>
            Add
          </Button>
        </form>
      </div>
    </Panel>
  );
}

function Macs({ devices }: { devices: AssistantDeviceItem[] }) {
  const qc = useQueryClient();
  const [pairing, setPairing] = useState<AssistantPairing | null>(null);
  const [removing, setRemoving] = useState<AssistantDeviceItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["assistant"] });
  return (
    <Panel>
      <PanelHeader
        title="Your Mac"
        description="A small helper on your Mac reads Messages (your iPhone's texts, through iCloud) and sends the replies you approve from your number."
        action={
          <Button
            onClick={async () => {
              setError(null);
              try {
                setPairing(await post<AssistantPairing>("/api/assistant/pairing"));
              } catch (err) {
                setError(errorMessage(err));
              }
            }}
          >
            Connect a Mac
          </Button>
        }
      />
      <div className="px-5 py-4">
        {error && <Alert>{error}</Alert>}
        {devices.length ? (
          <ul className="flex flex-col divide-y divide-mist">
            {devices.map((d) => {
              const quiet = !d.lastSeenAt || Date.now() - new Date(d.lastSeenAt).getTime() > 15 * 60_000;
              return (
                <li key={d.id} className="flex flex-col gap-1 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{d.name || "Mac"}</span>
                    {d.paused ? <Badge tone="off">Paused</Badge> : quiet ? <Badge tone="wait">Not checking in</Badge> : <Badge tone="ok">Connected</Badge>}
                    <span className="text-xs text-muted">{d.lastSeenAt ? `Last checked in ${ago(d.lastSeenAt)}` : "Hasn't checked in yet"}</span>
                    <span className="ml-auto flex gap-2">
                      <Button className="px-3 py-1" onClick={async () => (await patch(`/api/assistant/devices/${d.id}`, { paused: !d.paused }).catch(() => undefined), refresh())}>
                        {d.paused ? "Resume" : "Pause"}
                      </Button>
                      <Button className="px-3 py-1" onClick={() => setRemoving(d)}>
                        Remove…
                      </Button>
                    </span>
                  </div>
                  {d.problem && <Alert tone="info">{d.problem}</Alert>}
                  {quiet && !d.paused && !d.problem && <p className="text-xs text-muted">The Mac needs to be on, logged in, and online. If it's been restarted, open NMT Assistant from its Applications folder.</p>}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted">No Mac connected yet. Use your office Mac that's signed in to Messages with your Apple ID (with Messages in iCloud and Text Message Forwarding on).</p>
        )}
      </div>
      {pairing && <PairDialog pairing={pairing} onClose={() => (setPairing(null), refresh())} />}
      {removing && (
        <Dialog
          title={`Remove ${removing.name || "this Mac"}?`}
          onClose={() => setRemoving(null)}
          footer={
            <>
              <Button onClick={() => setRemoving(null)}>Cancel</Button>
              <Button variant="danger" onClick={async () => (await del(`/api/assistant/devices/${removing.id}`).catch(() => undefined), setRemoving(null), refresh())}>
                Remove
              </Button>
            </>
          }
        >
          <p className="text-sm">It stops sending texts right away. To take the helper off the Mac too, paste this into its Terminal:</p>
          <Copyable text={`curl -fsSL ${window.location.origin}/api/public/assistant/files/uninstall.sh | bash`} />
        </Dialog>
      )}
    </Panel>
  );
}

function Copyable({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-2 flex items-start gap-2">
      <code className="min-w-0 flex-1 rounded-[var(--radius-control)] bg-ink px-3 py-2 font-mono text-xs break-all text-white">{text}</code>
      <Button className="shrink-0 px-3 py-1.5" onClick={() => void navigator.clipboard.writeText(text).then(() => (setCopied(true), setTimeout(() => setCopied(false), 2000)))}>
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}

function PairDialog({ pairing, onClose }: { pairing: AssistantPairing; onClose: () => void }) {
  return (
    <Dialog title="Connect a Mac" onClose={onClose} wide footer={<Button onClick={onClose}>Done</Button>}>
      <ol className="flex list-decimal flex-col gap-3 pl-5 text-sm">
        <li>On the Mac, open <b>Terminal</b> (Applications → Utilities), paste this, and press Return:</li>
      </ol>
      <Copyable text={pairing.command} />
      <ol start={2} className="mt-3 flex list-decimal flex-col gap-2 pl-5 text-sm">
        <li>
          It connects, installs <b>NMT Assistant</b> (it starts when you log in), and opens System Settings at <b>Full Disk Access</b>. Click <b>+</b>, choose your Home folder → Applications → <b>NMT Assistant</b>, and turn it on. Then press Return in Terminal.
        </li>
        <li>The first time Fred sends a reply you approved, the Mac asks to let NMT Assistant control Messages: click <b>OK</b>.</li>
      </ol>
      <p className={cx("mt-3 text-xs text-muted")}>This code works once, until {new Date(pairing.expiresAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}. Texts from the last week come in for context; Fred only makes suggestions about new ones.</p>
    </Dialog>
  );
}

function Prefs({ prefs }: { prefs: AssistantOverview["prefs"] }) {
  const qc = useQueryClient();
  const [limit, setLimit] = useState(String(prefs.dailyLimit));
  const save = async (next: AssistantOverview["prefs"]) => {
    await put("/api/assistant/prefs", next).catch(() => undefined);
    void qc.invalidateQueries({ queryKey: ["assistant"] });
  };
  return (
    <Panel>
      <PanelHeader title="Settings" />
      <div className="flex flex-col gap-3 px-5 py-4">
        <Checkbox label="Fred looks at my new texts" description="Off: texts still come in (for context), but no suggestions." checked={prefs.on} onChange={(on) => void save({ ...prefs, on })} />
        <div className="flex items-end gap-2">
          <Field label="At most this many new suggestions a day" type="number" min={1} max={100} value={limit} onChange={(e) => setLimit(e.target.value)} className="w-64" />
          <Button disabled={Number(limit) === prefs.dailyLimit || !(Number(limit) >= 1 && Number(limit) <= 100)} onClick={() => void save({ ...prefs, dailyLimit: Math.round(Number(limit)) })}>
            Save
          </Button>
        </div>
      </div>
    </Panel>
  );
}
